// Core packages
import { AfterViewInit, Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { FormGroup } from "@angular/forms";

// Third party packages
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from "@angular/material";
import { FormlyFieldConfig, FormlyFormOptions } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import { MatProgressButtonOptions } from "mat-progress-buttons";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { from, of, Subscription } from "rxjs";
import {
  catchError, concatMap, finalize, map, tap, toArray
} from "rxjs/operators";

// Custom packages
import { AuthService } from "src/app/modules/auth/auth.service";
import { PropertiesService } from "src/app/modules/properties/properties.service";
import { UsersService } from "src/app/modules/users/users.service";
import { PrintQrDialogComponent } from 'src/app/shared/components/dialogs/print-qr-dialog/print-qr-dialog.component';
import { IUser } from "src/app/shared/models/user/user.interface";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from "src/app/shared/services/helper.service";
import { DocumentArchivesService } from "../../document-archives.service";
import { SameCodeEditTarget } from "../../same-code-docs-edit.utils";

/**
 * Script start
 */

/** Outcome of the save of one same-code row */
interface SameCodeSaveResult {
  target: SameCodeEditTarget;
  saved: boolean;
  /** HTTP error of the call; absent when the back-end answered but wrote nothing */
  error?: unknown;
}

@Component({
  selector: "app-property-detail-document-item-dialog",
  templateUrl: "./document-item-dialog.component.html",
  styleUrls: ["./document-item-dialog.component.scss"]
})
export class PropertyDetailDocumentItemDialogComponent
  implements OnInit, OnDestroy, AfterViewInit {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  loggedUserPermissions: string[];
  lang = this.translateService.currentLang || this.translateService.defaultLang;
  dialogIsLoading = true; // Used to view/hide form loading spinner

  // BEGIN - Edit item form
  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  // END - ngx-formly
  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.save"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-save",
      inline: true
    }
  };
  printModuleBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.printModule"),
    spinnerSize: 19,
    raised: false,
    stroked: false,
    flat: true,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-print",
      inline: true
    }
  };
  printQrBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.printLabel"),
    spinnerSize: 19,
    raised: false,
    stroked: false,
    flat: true,
    fab: false,
    buttonColor: "accent",
    spinnerColor: "accent",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-qrcode",
      inline: true
    }
  };
  // END - Edit item form
  existingNotes: any[] = [];
  barCode = "";
  qrcodeData = "";
  createdAt = "";
  createdBy = "";
  contract = false;
  expired = false;
  pdfMsgUrl: any[] = [];
  previousFileValue: any;
  userDataSetted = false;
  userData: any;
  docType: string;
  docUploaded = false;
  docsSelected: any;
  // "Modifica dati aggiuntivi" on several rows with the same code (property documents page only):
  // the rows the form is saved on, the ones the last save could not write (by subDocId), the form
  // value of the last save as sent, whether a save is running, whether some row has been written
  sameCodeTargets: SameCodeEditTarget[] = [];
  sameCodeFailed: { [subDocId: string]: boolean } = Object.create(null);
  sameCodeLastBody: string | null = null;
  sameCodeSaving = false;
  sameCodeSaved = false;
  // userData = {
  //   createdAt: "",
  //   createdBy: {
  //     _id: "",
  //     firstName: "",
  //     lastName: "",
  //   },
  //   // updatedAt: "",
  //   // updatedBy: {
  //   //   _id: "",
  //   //   firstName: "",
  //   //   lastName: "",
  //   // },
  // }

  /**
   * Class constructor
   */
  constructor(
    public dialogRef: MatDialogRef<PropertyDetailDocumentItemDialogComponent>,
    public cancelContractDialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private configService: ConfigService,
    private propertiesService: PropertiesService,
    private translateService: TranslateService,
    private authService: AuthService,
    private cookieService: CookieService,
    private dialog: MatDialog,
    private toastrService: ToastrService,
    private helperService: HelperService,
    private documentArchivesService: DocumentArchivesService,
    private usersService: UsersService,
  ) { }

  /**
   * Init components
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe(res => (this.loggedUser = res))
    );
    this.subscriptions.push(
      this.authService.loggedUserPermissions$.subscribe(
        res => (this.loggedUserPermissions = res)
      )
    );

    // Only admins/operators can choose between digital/paper documents
    if (
      (this.authService.loggedUserRequestedRoleType$.getValue() === "master"
        || this.authService.loggedUserRequestedRoleType$.getValue() === "collaborator")
      || (
        this.authService.loggedUserRequestedRoleType$.getValue() === "admin"
        || this.authService.loggedUserRequestedRoleType$.getValue() === "operator"
        && (this.data.action === "show" || this.data.action === "edit")
      )
    ) {
      this.docType = "digital";
      this.onChooseDocType(this.docType);
    }

    // Get current lang
    this.lang =
      this.cookieService.get("lang") || this.configService.defaultLang;

    this.barCode = this.data.barCode;

    // Rows ticked in the same-code selection: when there are any, Save writes each of them
    this.sameCodeTargets = Array.isArray(this.data.sameCodeTargets)
      ? this.data.sameCodeTargets.slice()
      : [];

    this.dialogIsLoading = false;

  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * Handle after view init lifecycle
   *
   * @since 1.0.0
   */
  ngAfterViewInit(): void {
    if (
      this.data.action === 'new'
      || this.data.action === 'edit'
    ) {
      this.watchFileChanges();
    }
  }

  /**
   * Init form basing on docType
   *
   * @since 1.0.0
   */
  onChooseDocType(type: string): void {
    this.docType = type;
    this.initForm();

    setTimeout(() => {
      if (
        this.docType === 'digital'
        && (this.data.action === 'new' || this.data.action === 'edit')
      ) {
        this.watchFileChanges();
      }
    }, 500);
  }

  /**
   * Init form
   *
   * @since 1.0.0
   */
  initForm(): void {
    if (this.data) {
      // Check if this document is a contract (if not let's hide cancellation button)
      if (this.data.contract) {
        this.contract = this.data.contract;
      }
      if (this.data.expired) {
        this.expired = this.data.expired;
      }
      if (this.data.contractPdfMsg && Array.isArray(this.data.contractPdfMsg)) {
        this.data.contractPdfMsg.forEach((item: any) => {
          const path = item.msg.replace(/src[\\\/]assets/g, 'public');
          let url = "";
          // if (path.substring(0,22) === "https://app.archedi.it") {
          //   url = path;
          // } else {
          //   url = `${this.configService.apiHost}/${path}`;
          // }
          url = `${this.configService.apiHost}/${path}`;
          this.pdfMsgUrl.push({
            url: url,
            sent: item.sent
          });
        })
      }
      if (this.data.itemData) {
        this.model = this.data.itemData;

        if (this.data.itemData.note) {
          this.existingNotes = this.data.itemData.note;
          this.data.itemData.note = null;
        }

        // Get user name
        if (this.data.itemData.createdBy) {
          this.usersService.get(this.data.itemData.createdBy).subscribe(
            res => {
              if (res.status) {
                this.userData = {
                  createdAt: this.data.itemData.createdAt,
                  createdBy: {
                    _id: this.data.itemData.createdBy,
                    firstName: res.data.firstName,
                    lastName: res.data.lastName,
                  },
                  // updatedAt: "",
                  // updatedBy: {
                  //   _id: "",
                  //   firstName: "",
                  //   lastName: "",
                  // },
                }
                if (
                  this.data.contract && 
                  this.data.itemData.cancellationRequestBy
                ) {
                  this.usersService.get(this.data.itemData.cancellationRequestBy).subscribe(
                    res => {
                      if (res.status) {
                        this.userDataSetted = true;
                        this.userData.cancellationRequestDate = this.data.itemData.cancellationRequestDate;
                        this.userData.cancellationRequestBy = {
                          _id: this.data.itemData.cancellationRequestBy,
                          firstName: res.data.firstName,
                          lastName: res.data.lastName,
                        };

                        if (
                          this.data.contractPdfMsg && 
                          Array.isArray(this.data.contractPdfMsg) && 
                          this.data.contractPdfMsg.length > 0
                        ) {
                          this.userData.cancellation = true;
                        } else {
                          this.userData.cancellation = false;
                        }
                      }
                    });
                } else {
                  this.userDataSetted = true;
                }
              }
            });
        } else {
          this.userData = {
            createdAt: this.data.itemData.createdAt,
            createdBy: {
              _id: this.data.itemData.createdBy,
              firstName: "",
              lastName: "",
            }
          }
        }
      } else {
        this.contract = false;
        this.userData = {
          createdAt: undefined,
          createdBy: {
            _id: undefined,
            firstName: "",
            lastName: "",
          }
        }
      }

      if (this.data.itemConfig && this.data.itemConfig) {
        this.fields = this.data.itemConfig;
        if (this.docType && this.docType === 'digital') {
          if (this.data.action !== "show" && this.data.action !== "edit") {
            this.fields.push(
              {
                fieldGroupClassName: "row",
                fieldGroup: [
                  {
                    className: "col-12",
                    type: "file-uploader",
                    key: "documentFile",
                    templateOptions: {
                      required: true,
                      allowedFilesNumber: 200,
                      allowedFilesExtensions: this.configService.allowedDocumentExtensions,
                      customLabel: this.translateService.instant('properties.detail.documentArchive.fields.file'),
                    },
                    // Show file upload only if file is not uploaded yet
                    hideExpression:
                      typeof this.data.itemData !== 'undefined'
                      && this.data.itemData.document
                      && this.data.itemData.document._id !== ''
                  }
                ]
              }
            );
          }
        } else if (this.docType && this.docType === 'paper') {
          // For better fields displaying
          // this.dialogRef.updateSize('60vw');
          this.fields.push(
            {
              fieldGroupClassName: "row",
              fieldGroup: [
                {
                  className: "col-6",
                  type: "input",
                  key: "numFiles",
                  hooks: {
                    onInit: (field: any): void => {
                      field.formControl.valueChanges
                        .pipe(
                          tap(value => {
                            if (typeof value === "undefined") {
                              value = null;
                            }
                            if (value > field.templateOptions.max) {
                              value = field.templateOptions.max;
                            }
                            if (this.model.files) {
                              this.model.files.length = value;
                            }
                            this.model = {
                              ...this.model,
                              numFiles: value
                            };
                          })
                        )
                        .subscribe();
                    }
                  },
                  templateOptions: {
                    type: "number",
                    label: this.translateService.instant('properties.detail.documentArchive.fields.numFiles'),
                    required: false,
                    min: 2,
                    max: 100,
                  },
                  expressionProperties: {
                    // apply expressionProperty for disabled based on formState
                    "templateOptions.disabled": "formState.disabled"
                  },
                },
                {
                  className: "docs-radio-group-wrapper col-12",
                  key: "files",
                  type: "repeat",
                  expressionProperties: {
                    // apply expressionProperty for disabled based on formState
                    "templateOptions.disabled": "formState.disabled"
                  },
                  fieldArray: {
                    fieldGroup: [
                      {
                        className: "pl-0 col-12 col-md-6",
                        type: "input",
                        key: "name",
                        hooks: {
                          onInit(field) {
                            const type = field.parent.fieldGroup[1].defaultValue[0]; // take first letter from radio's defaultValue
                            const name = this.barCode; // barcode value
                            const index = +field.parent.key + 1; // actual array index plus 1
                            field.defaultValue = `${name}_${type}${index}`.toUpperCase();
                          },
                        },
                        templateOptions: {
                          label: this.translateService.instant('properties.detail.documentArchive.fields.name'),
                          placeholder: this.translateService.instant('properties.detail.documentArchive.fields.name'),
                          minLength: 0,
                          maxLength: 500,
                          readonly: true,
                        },
                      },
                      {
                        className: "col-12 col-md-6",
                        type: "radio",
                        key: "docType",
                        defaultValue: "report",
                        expressionProperties: {
                          "templateOptions.onChange": (model: any, opt: any, field: any) => {
                            if (model) {
                              const position = +field.parent.key;

                              // @todo: da rivedere (segnalato in #105)
                              // Return if no changes were made
                              // if (
                              //   this.previousDocTypes.map(el => el.value).includes(field.formControl.value)
                              //   && this.previousDocTypes.map(el => el.position).includes(position)
                              // ) {
                              //   return;
                              // }
                              // this.previousDocTypes = [];
                              // field.parent.parent.fieldGroup.forEach(el => {
                              //   this.previousDocTypes.push({
                              //     position,
                              //     value: el.formControl.value.docType
                              //   });
                              // });

                              const type = model.docType[0]; // take first letter from radio's actual value
                              const name = this.barCode; // barcode value
                              const index = position + 1; // actual array index plus 1;

                              // Set new value in model
                              model.name = `${name}_${type}${index}`.toUpperCase();

                              // Set new value in field (make it change inside form input)
                              field.parent.fieldGroup[0].formControl.setValue(model.name);
                            }
                          },
                        },
                        templateOptions: {
                          label: this.translateService.instant('properties.detail.documentArchive.fields.docType'),
                          options: [
                            {
                              value: "report",
                              label: this.translateService.instant('properties.detail.documentArchive.fields.report'),
                            },
                            {
                              value: "tables",
                              label: this.translateService.instant('properties.detail.documentArchive.fields.tables'),
                            },
                            {
                              value: "calcs",
                              label: this.translateService.instant('properties.detail.documentArchive.fields.calcs'),
                            },
                            {
                              value: "schemas",
                              label: this.translateService.instant('properties.detail.documentArchive.fields.schemas'),
                            },
                            {
                              value: "various",
                              label: this.translateService.instant('properties.detail.documentArchive.fields.various'),
                            }
                          ],
                        }
                      },
                    ]
                  }
                }
              ]
            }
          );
        } else {
          this.toastrService.error('Error: no docType selected');
        }
      }

      // QRcode.
      // Attention: not per "contratti"
      const section = this.data.documentTypeRoute;
      const host = this.configService.frontEndHost;
      const documentId = this.data.documentId ? this.data.documentId : 'generic';
      if (
        section !== "locazione-contrdi-locazione" &&
        section !== 'locazioneContrdiLocazione' && 
        section !== "contratti" && 
        !this.contract
      ) {
        this.qrcodeData =
          `${host}/qrcode/${this.data.documentArchiveId}/${section}/${documentId}/${this.data.documentKey}/${this.data.subDocumentId}`;
        // this.qrcodeData = JSON.stringify({
        //   archive: this.data.documentArchiveId,
        //   section,
        //   doc: this.data.documentId,
        //   key: this.data.documentKey,
        //   subDoc: this.data.subDocumentId,
        // });
      }
    } else {
      // this.toastrService.error('Errore nel recupero dei dati');
      // this.onClose(null);
    }

    this.userDataSetted = true;
  }

  /**
   * Watch for file-uploader changes, if file was uploaded
   * scroll down on dialog bottom
   *
   * @since 1.0.0
   */
  watchFileChanges(): void {
    if (this.form.controls["documentFile"]) {
      this.subscriptions.push(
        this.form.controls["documentFile"].valueChanges.subscribe(
          res => {
            // A file was uploaded, scroll down dialog
            if (Array.isArray(res) && res.length) {
              const dialogWindow = document.getElementsByTagName("mat-dialog-container")[0]; // assuming we can have only one dialog opened
              if (dialogWindow) {
                const contentToScroll = dialogWindow.getElementsByClassName("mat-dialog-content");
                if (contentToScroll[0]) {
                  const bottomHeight = contentToScroll[0].scrollHeight;
                  contentToScroll[0].scrollTo({
                    top: bottomHeight,
                    behavior: "smooth",
                  });
                }
              }
            }
          },
          err => {
            this.helperService.handleError(err);
          }
        ));
    }
  }

  /**
   * Handle dialog closing
   *
   * @since 1.0.0
   */
  onClose(data?: any): void {
    // Closing in the middle of a same-code save would cancel the rows not written yet
    if (this.sameCodeSaving) {
      return;
    }
    // Some same-code rows are already written: the page has to reload whatever way the dialog closes
    this.dialogRef.close(typeof data === "undefined" && this.sameCodeSaved ? "reload" : data);
  }

  /**
   * Handle form submit
   *
   * @since 1.0.0
   */
  onFormSubmit(): void {

    // Prevent invalid form submit
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach(key => {
        this.form.controls[key].markAsTouched();
      });
      console.warn("form", this.form);
      this.toastrService.error(
        this.translateService.instant("common.errors.form_is_invalid")
      );
      // Set errors inside invalid form controls
      Object.keys(this.form.controls).forEach(key => {
        const control = this.form.controls[key];
        if (control.invalid) {
          control.markAsTouched();
          control.setErrors({
            required: true,
          });
        }
      });
      return;
    }

    // Activate submit button loading status
    this.formBtnOptions.active = true;

    let dataToSend = {};
    if (this.form.value) {
      let structureSelected: any[];
      // check contracts section
      const section = this.data.documentTypeRoute;
      if (section === 'contratti') {
        structureSelected = [];
      }
      if (this.data.structureId) {
        const entry: any = { id: this.data.structureId, value: true };
        if (
          this.data.itemData &&
          this.data.itemData.structureSelected &&
          Array.isArray(this.data.itemData.structureSelected)
        ) {
          const match = this.data.itemData.structureSelected.find(
            (el: any) => el.id.toString() === this.data.structureId.toString()
          );
          if (match) {
            entry.name = match.name;
            entry.type = match.type;
            if (match.typeStructure) {
              entry.typeStructure = match.typeStructure;
            }
          }
        }
        structureSelected = [entry];
      }
      if (this.docType === 'digital') {
        this.docsSelected = [{
          subDoc: this.data.keyTranslateTitle,
          subAreaTitle: "",
          title: this.data.modalTitle.trim(),
          key: this.data.documentKey,
          structureSelected
        }];
        
        let isContract = false;
        if (
          this.data.documentKey === "HA6" ||
          this.data.documentKey === "HA7" ||
          this.data.documentKey === "HA8" ||
          this.data.documentKey === "HA12" ||
          this.data.documentKey === "HA64" ||
          this.data.documentKey === "HA44" ||
          this.data.documentKey === "HA48" ||
          this.data.documentKey === "HA63" ||
          this.data.documentKey === "HA62"
        ) {
          isContract = true;
        }

        if (this.data.action && this.data.action === "new") {
          const sendData = {
            data: this.form.value,
            docsSelected: this.docsSelected,
            structureSelected,
            documentFile: this.form.value.documentFile,
            contratti: isContract,
          }
          this.subscriptions.push(
            this.documentArchivesService
              .updateMultiDoc(
                this.data.documentArchiveId,
                sendData
              )
              .pipe(finalize(() => (this.formBtnOptions.active = false)))
              .subscribe(
                res => {
                  if (res.status) {
                    if (res.data && typeof res.data === "string") {
                      this.barCode = res.data.toUpperCase();
                      this.docUploaded = true;
                    }
                    this.toastrService.success(res.message);
                    // this.dialogRef.close({
                    //   save: "success"
                    // });
                  }
                },
                err => {
                  this.helperService.handleFormlyError(this.form, err);
                }
              )
          );
        } else if (this.sameCodeTargets.length) {
          this.saveOnSameCodeTargets();
        } else {
          const sendData = {
            docKey: this.data.documentKey,
            subDocid: this.data.subDocumentId,
            data: this.form.value,
            note: this.form.value.note,
            documentFile: this.form.value.documentFile,
            propertyCode: this.data.propertyCode,
            companyCode: this.data.companyCode,
          }
          this.subscriptions.push(
            this.documentArchivesService
              .updateSingleDoc2(
                this.data.documentArchiveId,
                this.data.documentTypeRoute,
                this.data.structureId,
                sendData
              )
              .pipe(finalize(() => {
                this.formBtnOptions.active = false}
              ))
              .subscribe(
                res => {
                  if (res.status) {
                    this.onClose("reload");
                    // if (res.data && typeof res.data === "string") {
                    //   this.barCode = res.data.toUpperCase();
                    //   this.docUploaded = true;
                    //   this.toastrService.success(res.message);
                    // }
                    // this.dialogRef.close({
                    //   save: "success"
                    // });
                  }
                },
                err => {
                  this.helperService.handleFormlyError(this.form, err);
                }
              )
          );
        }
      } else if (this.docType === 'paper') {
        // First file is not required and name is equal to barcode - 2021-07-10
        if (!this.model.numFiles || !this.model.files) {
          this.model.numFiles = 1;
          this.model.files = [{
            docType: "",
            name: this.barCode
          }];
        }
        const docsSelected = [{
          subDoc: this.data.keyTranslateTitle,
          subAreaTitle: "",
          title: this.data.modalTitle.trim(),
          key: this.data.documentKey,
          structureSelected
        }];
        dataToSend = {
          data: {
            ...this.model,
            property: this.data.propertyId,
            code: this.barCode,
            area: this.data.selectedArea
          },
          docsSelected,
          documentFile: this.form.value.documentFile,
        }

        if (structureSelected && structureSelected.length) {
          dataToSend = {
            ...dataToSend,
            structureSelected,
          };
        }
        this.subscriptions.push(
          this.documentArchivesService
            .updateMultiPaperDoc(
              this.data.documentArchiveId,
              dataToSend,
            )
            .pipe(finalize(() => (this.formBtnOptions.active = false)))
            .subscribe(
              res => {
                if (res.status) {
                  this.docUploaded = true;
                  this.toastrService.success(res.message);
                }
              },
              err => {
                this.helperService.handleError(err);
                this.helperService.handleFormlyError(this.form, err);
              }
            )
        );
      }
    }
  }

  /**
   * Save the form on every row ticked in the same-code selection, one update-single-doc call per row.
   *
   * The calls run one after the other: each of them recomputes rules over the whole property
   * (obligations, expirations, supplier counters) and two at once would race on the same documents.
   * Saving the same values twice writes nothing new (no file, no invoice row, no e-mail), so after a
   * partial save the dialog stays open and Save retries the rows that failed. If the form changed in
   * the meantime every row is saved again, so that all of them end up with the same values.
   *
   * @since 3.0.0
   */
  private saveOnSameCodeTargets(): void {
    if (this.sameCodeSaving) {
      return;
    }
    // The body exactly as HttpClient would serialize it, taken once: the form stays editable while
    // the calls run, and every row has to receive the same values.
    const body = JSON.stringify(this.form.value);
    const failedOnly = this.sameCodeTargets.filter((target) => this.sameCodeFailed[target.subDocId]);
    const targets =
      this.sameCodeLastBody === body && failedOnly.length ? failedOnly : this.sameCodeTargets.slice();
    this.sameCodeLastBody = body;
    const formValue = JSON.parse(body);

    this.sameCodeSaving = true;
    this.dialogRef.disableClose = true;
    this.subscriptions.push(
      from(targets)
        .pipe(
          concatMap((target: SameCodeEditTarget) =>
            this.documentArchivesService
              .updateSingleDoc2(
                this.data.documentArchiveId,
                target.documentTypeRoute,
                target.structureId,
                {
                  docKey: target.docKey,
                  subDocid: target.subDocId,
                  data: formValue,
                  note: formValue.note,
                  documentFile: formValue.documentFile,
                  propertyCode: this.data.propertyCode,
                  companyCode: this.data.companyCode,
                }
              )
              .pipe(
                // A row that matches nothing still gets 200, with an empty barCode: nothing was written
                map((res: any): SameCodeSaveResult => ({
                  target,
                  saved: !!(res && res.status && res.data && res.data.barCode),
                })),
                catchError((error: unknown) => of<SameCodeSaveResult>({ target, saved: false, error }))
              )
          ),
          tap((result: SameCodeSaveResult) => {
            if (result.saved) {
              this.sameCodeSaved = true;
            }
          }),
          toArray(),
          finalize(() => {
            this.sameCodeSaving = false;
            this.formBtnOptions.active = false;
            // After a partial save only the dialog buttons may close it, so that the page reloads
            this.dialogRef.disableClose = this.sameCodeSaved;
          })
        )
        .subscribe((results: SameCodeSaveResult[]) => this.onSameCodeSaveResults(results))
    );
  }

  /**
   * Close when every row holds the values of the form; otherwise mark the rows still to write
   *
   * @since 3.0.0
   */
  private onSameCodeSaveResults(results: SameCodeSaveResult[]): void {
    this.sameCodeSaving = false;
    results.forEach((result) => {
      this.sameCodeFailed[result.target.subDocId] = !result.saved;
    });
    const failed = this.sameCodeTargets.filter((target) => this.sameCodeFailed[target.subDocId]);

    if (!failed.length) {
      this.toastrService.success(
        this.translateService.instant("properties.dialog.docEditSameCodeSaved", {
          count: this.sameCodeTargets.length,
        })
      );
      this.onClose("reload");
      return;
    }

    const withError = results.filter((result) => typeof result.error !== "undefined")[0];
    if (withError) {
      // Same handling as the single save: field errors of a 400 land on the form, the rest is a toast
      this.helperService.handleFormlyError(this.form, withError.error);
    }
    this.toastrService.error(
      this.translateService.instant("properties.dialog.docEditSameCodePartial", {
        saved: this.sameCodeTargets.length - failed.length,
        total: this.sameCodeTargets.length,
        docs: failed.map((target) => target.label).join(", "),
      })
    );
  }

  /**
   * Handle print file qrCode
   *
   * @since 1.0.0
   */
  onPrint(target: string): void {
    const qrCodes = [];
    if (this.model.files && this.model.files.length) {
      // If we're here we have a paper doc
      this.model.files.forEach(file => {
        if (file.name) {
          qrCodes.push(file.name);
        }
      });
    } else if (this.model.documentFile && this.model.documentFile.length) {
      // If we're here we have a digital doc
      qrCodes.push(this.barCode);
    } else {
      // Paper doc: First file is not required and name is equal to barcode - 2021-07-10
      qrCodes.push(this.barCode);
    }

    if (!qrCodes.length) {
      this.toastrService.error("No qrCodes to inject");
      return;
    }

    let data;

    if (target === "qr") {
      data = {
        target,
        qrCodes,
        propertyName: this.data.property.name,
        propertyCity: this.data.property.address.city,
        propertyAddress: `${this.data.property.address.route}, ${this.data.property.address.streetNumber}`,
        area: this.data.selectedArea,
        docs: this.docsSelected,
      };
    } else if (target === "module") {
      data = {
        target,
        qrCodes,
        propertyName: this.data.property.name,
        propertyCity: this.data.property.address.city,
        propertyAddress: `${this.data.property.address.route}, ${this.data.property.address.streetNumber}`,
        area: this.data.selectedArea,
        uploadDate: new Date(),
        content: this.model.contenutoDocumento,
        docs: this.docsSelected,
        structures: this.data.itemData && this.data.itemData.structureSelected
          ? this.data.itemData.structureSelected.filter(el => el.value) : undefined,
      };
    } else {
      this.toastrService.error("missing print target");
    }

    // Open dialog
    this.dialog.open(PrintQrDialogComponent, {
      data,
      width: "600px"
    });
  }

  /**
   * Handle save QR code as image
   *
   * @since 1.0.0
   */
  onSaveQRAsImage(parent): void {
    // fetches base 64 date from image
    const parentElement = parent.qrcElement.nativeElement.querySelector("img").src;

    // converts base 64 encoded image to blobData
    const blobData = this.convertBase64ToBlob(parentElement);

    // saves as image
    if (window.navigator && window.navigator.msSaveOrOpenBlob) { //IE
      window.navigator.msSaveOrOpenBlob(blobData, 'Qrcode');
    } else { // chrome
      const blob = new Blob([blobData], { type: "image/png" });
      const url = window.URL.createObjectURL(blob);
      // window.open(url);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Qrcode.png';
      link.click();
    }
  }

  /**
   * Convert image into Base64
   *
   * @since 1.0.0
   */
  private convertBase64ToBlob(Base64Image: any): Blob {
    // SPLIT INTO TWO PARTS
    const parts = Base64Image.split(';base64,');
    // HOLD THE CONTENT TYPE
    const imageType = parts[0].split(':')[1];
    // DECODE BASE64 STRING
    const decodedData = window.atob(parts[1]);
    // CREATE UNIT8ARRAY OF SIZE SAME AS ROW DATA LENGTH
    const uInt8Array = new Uint8Array(decodedData.length);
    // INSERT ALL CHARACTER CODE INTO UINT8ARRAY
    for (let i = 0; i < decodedData.length; ++i) {
      uInt8Array[i] = decodedData.charCodeAt(i);
    }
    // RETURN BLOB IMAGE AFTER CONVERSION
    return new Blob([uInt8Array], { type: imageType });
  }
}
